<script setup lang="ts">
// Layouts as in the FlowConnect dashboard: sidebar + topbar for the app, a centred card on a grid for sign-in.
import { TooltipProvider } from 'reka-ui'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Toaster } from 'vue-sonner'
import 'vue-sonner/style.css'
import Logo from './app/Logo.vue'
import MobileNav from './app/MobileNav.vue'
import Sidebar from './app/Sidebar.vue'
import Topbar from './app/Topbar.vue'
import { resolvedTheme } from './lib/theme'
import { loadMe, me } from './session'

const route = useRoute()
const router = useRouter()
const ready = ref(false)
const mobileOpen = ref(false)
const isPublic = computed(() => !!route.meta.public)
watch(
  () => route.fullPath,
  () => (mobileOpen.value = false),
)

onMounted(async () => {
  await router.isReady()
  if (!isPublic.value) await loadMe().catch(() => undefined)
  ready.value = true
})
</script>

<template>
  <TooltipProvider>
    <div v-if="isPublic" class="relative flex min-h-dvh items-center justify-center overflow-hidden px-4 py-12">
      <div
        class="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,hsl(262_83%_58%/0.35),transparent)] blur-2xl"
      />
      <div class="relative w-full max-w-[420px]">
        <div class="mb-8 flex justify-center"><Logo /></div>
        <RouterView />
        <p class="mt-8 text-center text-xs text-muted-foreground">FlowFront by Flowgate</p>
      </div>
    </div>

    <div v-else-if="ready && me" class="relative flex min-h-dvh">
      <div class="pointer-events-none fixed inset-x-0 top-0 -z-0 h-[420px] bg-[radial-gradient(60%_100%_at_50%_0%,var(--glow),transparent)]" />
      <Sidebar class="hidden lg:flex" />
      <MobileNav v-model:open="mobileOpen" />
      <div class="relative flex min-w-0 flex-1 flex-col lg:pl-64">
        <Topbar @open-menu="mobileOpen = true" />
        <main class="relative mx-auto w-full max-w-[1280px] flex-1 px-4 pt-6 pb-16 sm:px-6 lg:px-8">
          <RouterView v-slot="{ Component }">
            <Transition name="page" mode="out-in"><component :is="Component" /></Transition>
          </RouterView>
        </main>
      </div>
    </div>

    <div v-else class="flex min-h-dvh items-center justify-center text-sm text-muted-foreground">Loading…</div>
    <Toaster :theme="resolvedTheme" position="bottom-right" rich-colors />
  </TooltipProvider>
</template>
